import React, { useMemo } from 'react';
import { MdArrowBack } from 'react-icons/md';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import { useProfile } from '../contexts/ProfileContext';
import { computeVizFromProfile } from '../utils/computeVizFromProfile';
import financialSummaryData from '../data/financialSummary.json';

// BeHuman chart palette (May 2026 refresh) — see styles/tokens.md
// (--td-chart-1..8). Hex literals because Recharts passes these to SVG
// `fill` attrs which can't resolve CSS vars.
const VIZ_COLORS = [
  '#2FBF00', // chart-1: shield green (primary)
  '#A4D6DD', // chart-2: sky          (pastel cool)
  '#FEBFDA', // chart-3: pink         (pastel warm)
  '#FEEF60', // chart-4: yellow       (pastel bright)
  '#448889', // chart-5: teal         (deeper cool)
  '#002B1A', // chart-6: premium      (anchor)
  '#946E9F', // chart-7: lavender     (tail series)
  '#CFBD91', // chart-8: gold         (tail series)
];
const INCOME_COLORS = ['#2FBF00','#A4D6DD','#FEBFDA','#FEEF60'];

const FinancialSummaryScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();
  const vizData = useMemo(() => {
    if (loadedProfileData?.profile) {
      return computeVizFromProfile(loadedProfileData.profile, loadedProfileData.source) || financialSummaryData.financialSummaryData;
    }
    return financialSummaryData.financialSummaryData || null;
  }, [loadedProfileData]);

  const tipStyle = { background:'#17171a', border:'1px solid #2a2a2f', borderRadius:6, fontSize:11, color:'#f0ede8' };
  const fmt = v => `$${Number(v).toLocaleString()}`;

  if (!vizData) return null;

  const { customer, incomeData, donutData, ccMonthlyData, balanceData, fixedData, allCCCats, incomeCatKeys, stats, periodLabel, txnCount, acctCount, insights } = vizData;
  const customerName = customer.name || (customer.first_name && customer.last_name ? `${customer.first_name} ${customer.last_name}` : loadedProfileData?.name || 'Customer');
  const subtitle = [customer.employment?.occupation, customer.address?.city && customer.address?.province ? `${customer.address.city}, ${customer.address.province}` : null, customer.age ? `Age ${customer.age}` : null].filter(Boolean).join(' · ');

  return (
    <div className="screen viz-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back to home">
            <MdArrowBack />
          </button>
          <h1 className="bh-flow-title">Financial Summary</h1>
        </div>
      </header>

      <div className="scroll-content viz-content">

        <div className="viz-profile-header">
          <div>
            <div className="viz-label">Customer Profile · {loadedProfileData?.source || 'Default'}</div>
            <div className="viz-name">{customerName}</div>
            {subtitle && <div className="viz-subtitle">{subtitle}</div>}
          </div>
          <div style={{ textAlign:'right' }}>
            <div className="viz-label">Data Period</div>
            <div className="viz-period">{periodLabel}</div>
            <div className="viz-txn-count">{txnCount} transactions · {acctCount} accounts</div>
          </div>
        </div>

        <div className="viz-stat-grid">
          {[
            { label:'Avg Monthly Income', value: fmt(stats.avgMonthlyIncome), sub:'All income sources',    accent:'viz-yellow' },
            { label:'Total Saved',        value: fmt(stats.totalSaved),       sub:'Savings deposits',       accent:'viz-green' },
            { label:'CC Spend (6 mo)',    value: fmt(stats.totalCCSpend),     sub:'Credit card purchases',  accent:'viz-red' },
            { label:'Cash Back Earned',   value: fmt(stats.cashBack),         sub:'Rewards earned',         accent:'viz-blue' },
          ].map(({ label, value, sub, accent }) => (
            <div key={label} className={`viz-stat-card ${accent}`}>
              <div className="viz-stat-label">{label}</div>
              <div className="viz-stat-value">{value}</div>
              <div className="viz-stat-sub">{sub}</div>
            </div>
          ))}
        </div>

        {incomeData.some(m => incomeCatKeys.some(k => (m[k] || 0) > 0)) && (
          <div className="viz-chart-card">
            <div className="viz-chart-title">Monthly Income Breakdown</div>
            <div className="viz-chart-sub">{incomeCatKeys.join(' · ')}</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={incomeData} margin={{top:4,right:8,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2f" vertical={false}/>
                <XAxis dataKey="month" tick={{fontSize:11,fill:'#7a7a82'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:10,fill:'#7a7a82'}} axisLine={false} tickLine={false} tickFormatter={v=>`$${(v/1000).toFixed(1)}k`} width={42}/>
                <Tooltip formatter={(v,n)=>[`$${v.toLocaleString()}`,n]} contentStyle={tipStyle}/>
                <Legend wrapperStyle={{fontSize:10,paddingTop:8}} iconSize={8}/>
                {incomeCatKeys.map((cat, i) => (
                  <Bar key={cat} dataKey={cat} stackId="a" fill={INCOME_COLORS[i % INCOME_COLORS.length]} fillOpacity={0.8}
                    radius={i === incomeCatKeys.length - 1 ? [3,3,0,0] : undefined}/>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {donutData.length > 0 && (
          <div className="viz-chart-card">
            <div className="viz-chart-title">CC Spend by Category</div>
            <div className="viz-chart-sub">{periodLabel} totals</div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={donutData} dataKey="value" nameKey="name"
                     cx="50%" cy="45%"
                     innerRadius={72} outerRadius={90}
                     stroke="#000000" strokeWidth={1}>
                  {donutData.map((_, i) => <Cell key={i} fill={VIZ_COLORS[i % VIZ_COLORS.length]}/>)}
                </Pie>
                <Tooltip formatter={(v,n)=>[`$${v.toLocaleString()}`,n]} contentStyle={tipStyle}/>
                <Legend wrapperStyle={{fontSize:10}} iconSize={8}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {ccMonthlyData.length > 0 && allCCCats.length > 0 && (
          <div className="viz-chart-card">
            <div className="viz-chart-title">Monthly Credit Card Spend</div>
            <div className="viz-chart-sub">Stacked by category — {periodLabel}</div>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={ccMonthlyData} margin={{top:4,right:8,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2f" vertical={false}/>
                <XAxis dataKey="month" tick={{fontSize:11,fill:'#7a7a82'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:10,fill:'#7a7a82'}} axisLine={false} tickLine={false} tickFormatter={v=>`$${v}`} width={42}/>
                <Tooltip formatter={(v,n)=>[`$${v.toLocaleString()}`,n]} contentStyle={tipStyle}/>
                <Legend wrapperStyle={{fontSize:10,paddingTop:8}} iconSize={8}/>
                {allCCCats.map((cat, i) => (
                  <Bar key={cat} dataKey={cat} stackId="a" fill={VIZ_COLORS[i % VIZ_COLORS.length]} fillOpacity={0.85}
                    radius={i === allCCCats.length - 1 ? [3,3,0,0] : undefined}/>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {balanceData.some(d => d.Balance !== null) && (
          <div className="viz-chart-card">
            <div className="viz-chart-title">Chequing Balance Trend</div>
            <div className="viz-chart-sub">Month-end closing balances</div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={balanceData} margin={{top:4,right:16,left:0,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2f"/>
                <XAxis dataKey="month" tick={{fontSize:11,fill:'#7a7a82'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:10,fill:'#7a7a82'}} axisLine={false} tickLine={false} tickFormatter={v=>`$${v.toLocaleString()}`} width={52}/>
                <Tooltip formatter={(v)=>[`$${v.toLocaleString()}`,'Balance']} contentStyle={tipStyle}/>
                <Line type="monotone" dataKey="Balance" stroke="#CFBD91" strokeWidth={2.5} dot={{r:4,fill:'#CFBD91',strokeWidth:0}} activeDot={{r:6}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {fixedData.length > 0 && (
          <div className="viz-chart-card">
            <div className="viz-chart-title">Monthly Spending by Category</div>
            <div className="viz-chart-sub">Chequing debits — monthly average</div>
            <ResponsiveContainer width="100%" height={Math.max(160, fixedData.length * 34)}>
              <BarChart data={fixedData} layout="vertical" margin={{top:4,right:44,left:4,bottom:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2f" horizontal={false}/>
                <XAxis type="number" tick={{fontSize:10,fill:'#7a7a82'}} axisLine={false} tickLine={false} tickFormatter={v=>`$${v}`}/>
                <YAxis type="category" dataKey="name" tick={{fontSize:11,fill:'#7a7a82'}} axisLine={false} tickLine={false} width={80}/>
                <Tooltip formatter={(v)=>[`$${v.toLocaleString()}`,'Avg/month']} contentStyle={tipStyle}/>
                <Bar dataKey="value" radius={[0,3,3,0]}>
                  {fixedData.map((_, i) => <Cell key={i} fill={VIZ_COLORS[i % VIZ_COLORS.length]}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {insights.length > 0 && (
          <div className="viz-insights">
            {insights.map(({ icon, title, body }) => (
              <div key={title} className="viz-insight">
                <div className="viz-insight-icon">{icon}</div>
                <div className="viz-insight-title">{title}</div>
                <div className="viz-insight-body">{body}</div>
              </div>
            ))}
          </div>
        )}

        <div className="viz-footer">
          <span>IVA Demo · Internal Use Only · Synthetic Data</span>
          <span>Customer: {customerName}{customer.customer_id ? ` (ID: ${customer.customer_id})` : ''}</span>
        </div>

      </div>
    </div>
  );
};

export default FinancialSummaryScreen;
